import React, {type FunctionComponent} from 'react';

import {
  type FileInfo,
  fileInfoFromName,
  type PreviewTypes,
} from '../fileInfoFromName';
import type {
  FileBrowserFile,
  UseFileBrowserLoadFile,
  UseFileBrowserLoadFileUrl,
} from '../types';
import {CodePreview} from './CodePreview';
import {ImagePreview} from './ImagePreview';
import {JupyterPreview} from './JupyterPreview';
import {MarkdownPreview} from './MarkdownPreview';
import {NetronPreview} from './NetronPreview';
import {PatchPreview} from './PatchPreview';
import {UnknownFilePreview} from './UnknownFilePreview';

type PreviewComponent = FunctionComponent<{
  artifactId: string | null;
  file: FileBrowserFile;
  fileInfo: FileInfo;
  fileBody: string | undefined;
  onDownload(file: FileBrowserFile): void;
  useLoadFile: UseFileBrowserLoadFile;
  useLoadFileUrl: UseFileBrowserLoadFileUrl;
}>;

const FILE_TYPE_TO_PREVIEW_COMPONENT: Record<PreviewTypes, PreviewComponent> = {
  // FIXME: we only seem to use this component when we are trying to preview an
  //        artifact. I'm not sure when that case happens, but saying that
  //        the file type is invalid is not very helpful:
  artifact: UnknownFilePreview,
  code: CodePreview,
  patch: PatchPreview,
  image: ImagePreview,
  markdown: MarkdownPreview,
  netron: NetronPreview,
  // Note that Jupyter notebook previews don't use the downloaded file body,
  // choosing to re-download the file. But they are marked as "copyable",
  // because they cleanly copy into JSON.
  notebook: JupyterPreview,
  unknown: UnknownFilePreview,
};

interface FilePreviewProps {
  artifactId: string | null;
  useLoadFile: UseFileBrowserLoadFile;
  useLoadFileUrl: UseFileBrowserLoadFileUrl;
  file: FileBrowserFile;
  fileBody: string | undefined;
  onDownload(file: FileBrowserFile): void;
}

export function FilePreview({
  artifactId,
  useLoadFile,
  useLoadFileUrl,
  file,
  fileBody,
  onDownload,
}: FilePreviewProps) {
  const fileInfo = fileInfoFromName(file.name);

  const Preview = FILE_TYPE_TO_PREVIEW_COMPONENT[fileInfo.type];

  if (Preview) {
    return (
      <Preview
        artifactId={artifactId}
        file={file}
        fileInfo={fileInfo}
        fileBody={fileBody}
        onDownload={onDownload}
        useLoadFile={useLoadFile}
        useLoadFileUrl={useLoadFileUrl}
      />
    );
  }
  return <UnknownFilePreview file={file} onDownload={onDownload} />;
}
